import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { Tooltip } from 'react-tooltip';

import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { cn } from 'utils/cn';
import useCurrency from 'hooks/useCurreny';
import { BoardSteps } from 'utils/constants';
import { formatSwissNumber } from 'utils/formatNumber';
import { unMaskInputValue } from 'utils/helpers';
import { Country } from 'utils/types';
import {
  useGermanStatusBoard,
} from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/1Status/German/hooks/useGermanStatusBoard';
import {
  useStatusBoard,
} from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/1Status/Standart/hooks/useStatusBoard';
import {
  useMineralizationBoard,
} from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/5Mineralization/hooks/useMineralizationBoard';
import MaskInput from 'components/MaskedInput';
import { useCropBoard } from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/3Crop/hooks/useCropBoard';
import { useFertilizationInputs } from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/4Fertilization/hooks/useFertilizationInputs';
import { useFertilizationCalculations } from 'features/Croptimizer/screen/1Board/components/BoardSteps/components/4Fertilization/hooks/useFertilizationCalculations';

import SoilTypeField from '../Fields/SoilTypeField';
import CropTypeField from '../Fields/CropTypeField';
import CurrentStatusField from '../Fields/CurrentStatusField';
import TargetStatusField from '../Fields/TargetStatusField';
import DoseField from '../Fields/DoseField';
import FrequencyField from '../Fields/FrequencyField';

const MAINTENANCE_MODE_TOOLTIPS: Record<string, string> = {
  '1': 'Dose d\'entretien annuel',
  '2': 'Dose tous les 2 ans',
  '3': 'Dose tous les 3 ans',
  '4': 'Dose tous les 4 ans',
  '5': 'Dose tous les 5 ans',
};

const FREQUENCY_MODE_TOOLTIPS: Record<string, string> = {
  '1': 'Dose de redressement',
  '2': '1/2 dose 2 ans de suite',
  '3': '1/3 dose 3 ans de suite',
  '4': '1/4 dose 4 ans de suite',
  '5': '1/5 dose 5 ans de suite',
};

interface SummarySectionProps {
  isPDF: boolean;
}

interface SummaryCardProps {
  title: string;
  value?: string;
  unit?: string;
  className?: string;
  titleClass?: string;
  children?: ReactNode;
  tooltipId?: string;
  hasTooltip?: string | null | ReactNode;
}

const SummaryCard = ({ title, value, unit, className, children, tooltipId = '', hasTooltip = null, titleClass }: SummaryCardProps) => (
  <div className={cn('p-2 3xl:bg-gray-50 3xl:rounded-lg 2xl:bg-gray-50 2xl:rounded-lg md:mt-4', className)}>
    <div className={cn('font-bold mb-1.5', titleClass)}>
      {title}
      {hasTooltip && (
        <>
          {' '}
          <span data-tooltip-id={tooltipId} className="cursor-default font-black">
            ⓘ
          </span>
          <Tooltip
            id={tooltipId}
            place='top-start'
            className="tooltip"
            variant="light"
            noArrow
          >
            {hasTooltip}
          </Tooltip>
        </>
      )}
    </div>
    <div>
      {value && value}
      {unit && ` ${unit}`}
      {children && children}
    </div>
  </div>
);

export const SummarySection = ({ isPDF }: SummarySectionProps) => {
  const { t } = useTranslation();
  const {
    user,
    boardStatusValues,
    boardSoilValues,
    boardCropValues,
    fertilizationValues,
    step,
    mtHa,
    onlyAc,
    kgHa,
    activeTab,
    isExpertMode,
    isMaintenanceMode,
  } = useCroptimizer();
  const { currencyGreen } = useCurrency();
  
  const isGerman = user?.country?.name === Country.Germany;

  const {
    getOptimumValue,
    getTotalMtha,
  } = isGerman ? useGermanStatusBoard() : useStatusBoard();

  const {
    phAvg,
    setPhAvg,
    errorPhAvg,
    soilTillage,
    setSoilTillage,
    errorSoilTillage,
  } = useMineralizationBoard(isGerman);

  // Use the same hooks that the forms use to get real-time calculated values
  const { getYieldGainPrice } = useCropBoard();
  const {
    inputN,
    inputP,
    inputK,
    pricesN,
    pricesP,
    pricesK,
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    setEfficiencyN,
    setEfficiencyP,
    setEfficiencyK,
    cleaneffiN,
    cleaneffiP,
    cleaneffiK,
  } = useFertilizationInputs();

  // Call the calculations hook to trigger efficiency calculations
  useFertilizationCalculations(
    inputN,
    inputP,
    inputK,
    pricesN,
    pricesP,
    pricesK,
    cleaninputn,
    cleaninputp,
    cleaninputk,
    cleanpricesn,
    cleanpricesp,
    cleanpricesk,
    setEfficiencyN,
    setEfficiencyP,
    setEfficiencyK,
  );

  const handlePhAvgChange = (value: string) => {
    if (value.match(/\./g)) {
      const [, decimal] = value.split('.');

      if (decimal?.length > 1) {
        return;
      }
    }

    setPhAvg(value);
  };

  const getFrequencyTooltip = (): string => {
    const splitValue = boardSoilValues?.split || '';
    const tooltipMap = isMaintenanceMode ? MAINTENANCE_MODE_TOOLTIPS : FREQUENCY_MODE_TOOLTIPS;

    return tooltipMap[splitValue] || '';
  };

  // if (!boardStatusValues?.currentPh || step === BoardSteps.status) {
  //   return null;
  // }

  const showSummary = step !== BoardSteps.status;

  /*
   status = 'status',
  soil = 'soil',
  crop = 'crop',
  fertilization = 'fertilization',
  mineralization = 'mineralization',
  */
  // const showProductName = boardSoilValues?.productName && step !== BoardSteps.soil;

  const showProductDose =
    boardSoilValues?.totalMtha && step !== BoardSteps.soil;

  const showCropType = () => {
    if (isGerman) {
      return isExpertMode ? step !== BoardSteps.soil : true;
    } else {
      return step !== BoardSteps.crop;
    }
  };

  return (
    <>
      {showSummary && (
        <div
          className={cn(
            'border border-primitiveGrey rounded-2xl mb-6 grid gap-3 py-4 px-4',
            {
              '3xl:grid-cols-6 2xl:grid-cols-4 md:grid-cols-2 md:mt-2':
                !isPDF,
            },
            { 'gap-1 grid-cols-2 mt-2 py-2 px-4': isPDF },
          )}
        > 

          {isGerman &&
            <>
              {activeTab === 1 ? 
                <> 
                  {showCropType() && 
                            <SummaryCard
                              title={t('cropType')}
                              value={boardCropValues?.crop ?? ''}
                            /> 
                  }
                </>
                : 
                <SummaryCard title={t('cropType')}>
                  <CropTypeField isGerman={isGerman} titleHidden />
                </SummaryCard>
              }
            </>
          }

          {activeTab === 1 ? 
            <SummaryCard
              title={t('soilType')}
              value={boardSoilValues?.soilType ?? ''}
            /> :
            <SummaryCard title={t('soilType')}>
              <SoilTypeField titleHidden isGerman={isGerman}/>
            </SummaryCard>
          }

          {activeTab === 1 && boardStatusValues?.currentPh ?
            <div className="p-2 3xl:bg-gray-50 3xl:rounded-lg 2xl:bg-gray-50 2xl:rounded-lg">
              <div className="font-bold mb-1.5">{t('currentStatus')}</div>
              <div>{unMaskInputValue(boardStatusValues?.currentPh)} pH</div>
            </div>
            :
            <SummaryCard
              title={t('currentStatus')}>
              <CurrentStatusField isGerman={isGerman} withButtons titleHidden />
            </SummaryCard>
          }

          {activeTab === 1 && boardStatusValues?.targetPh ?
            <div className="p-2 3xl:bg-gray-50 3xl:rounded-lg 2xl:bg-gray-50 2xl:rounded-lg">
              <div className="font-bold mb-1.5">{t('targetStatus')}</div>
              <div>{unMaskInputValue(boardStatusValues?.targetPh)} pH</div>
            </div>
            :
            <SummaryCard
              title={t('targetStatus')}>
              <TargetStatusField isGerman={isGerman} withButtons titleHidden />
            </SummaryCard>
          }
          
          {/* {showProductName && (
            <SummaryCard
              title={t('productName')}
              value={boardSoilValues?.productName}
            />
          )} */}

          {showProductDose && (
            <SummaryCard
              title={t('productDose')}
              value={`${formatSwissNumber(Number(getTotalMtha))}${' '}${
                boardStatusValues?.targetPh === boardStatusValues?.currentPh
                  ? kgHa
                  : mtHa
              }`}
              titleClass={activeTab === 2 ? 'mb-[1.2rem]' : 'mb-1.5'}
            />
          )}

          {!isGerman &&
            <SummaryCard
              title={t('CAPoptimal')}
              value={getOptimumValue}
              titleClass={activeTab === 2 ? 'mb-[1.2rem]' : 'mb-1.5'}
            />
          }

          {!isGerman && 
            <>
              {activeTab === 1 ? 
                <> 
                  {showCropType() && 
                            <SummaryCard
                              title={t('cropType')}
                              value={boardCropValues?.crop ?? ''}
                            /> 
                  }
                </>
                : 
                <SummaryCard title={t('cropType')}>
                  <CropTypeField isGerman={isGerman} titleHidden />
                </SummaryCard>
              }
            </>
          }
          
          {/* {boardSoilValues?.cost && step !== BoardSteps.soil && (
            <SummaryCard
              title={t('productCost1')}
              value={`${formatSwissNumber(Number(boardSoilValues.cost))}${' '}
                ${currencyGreen}/${onlyAc}`}
            />
          )} */}

          {/* on each screen after crop section in expert mode OR only on FiveYearsBoard in NOT expert mode */}
          {!isPDF && ((boardCropValues?.crop && step !== BoardSteps.crop && step !== BoardSteps.soil && isExpertMode) ||
           (!isExpertMode && activeTab !== 1)) && (
            <>
              <div className="p-2 3xl:bg-gray-50 3xl:rounded-lg 2xl:bg-gray-50 2xl:rounded-lg md:mt-4">
                <div className={cn('font-bold mb-[1.2rem] break-words', {
                  'mb-1.5': activeTab !==2,
                })}>
                  {t('cropYieldGain')}
                </div>
                <div>
                  {formatSwissNumber(Number(getYieldGainPrice)) === '0' ? 'Voir Vue 5 ans' : formatSwissNumber(Number(getYieldGainPrice))}{' '}
                  {formatSwissNumber(Number(getYieldGainPrice)) === '0' ? '' : `${currencyGreen}/${onlyAc}`}
                </div>
              </div>
            </>
          )}

          {fertilizationValues?.efficiencyN &&
           (step === BoardSteps.mineralization || (step === BoardSteps.crop && !isExpertMode && activeTab === 2)) && (
            <SummaryCard
              title={`${t('npkEffiGain')}`}
              value={isMaintenanceMode ? 'Voir Vue 5 ans' : `${formatSwissNumber(
                Math.round(
                  Number(cleaneffiN) +
                  Number(cleaneffiP) +
                  Number(cleaneffiK),
                ),
              )} ${currencyGreen}/${onlyAc}`}
              titleClass={activeTab === 2 ? 'mb-[1.2rem]' : 'mb-1.5'}
            />
          )}

          {activeTab === 2 &&
            <>
              <SummaryCard
                title={t('dose') + ' (%)'}>
                <DoseField isGerman={isGerman} titleHidden />
              </SummaryCard>

              <SummaryCard
                title={isMaintenanceMode ? t('frequency') : t('splitInYear')}
                tooltipId="frequency-tooltip"
                hasTooltip={getFrequencyTooltip()}
              >
                <FrequencyField isGerman={isGerman} titleHidden />
              </SummaryCard>
            </>
          }
          {isExpertMode && activeTab === 2 && (
            <>
              <SummaryCard
                title={t('phAvgAnnualDecrease')}>
                <MaskInput
                  className="w-full"
                  value={phAvg}
                  onChange={(e) => handlePhAvgChange(e)}
                  redBorder={!!errorPhAvg}
                  integerLimit={1}
                  decimalLimit={2}
                  enforceDecimalPlaces
                  isBlueBg
                />
              </SummaryCard>

              <SummaryCard
                tooltipId="additional-tooltip"
                hasTooltip={
                  <div>
                    <div>{t('soiltooltip1')}</div>
                    <div>{t('soiltooltip2')}</div>
                  </div>
                }

                title={t('soilTillage') + ` (0 ${t('to')} 50%)`}>
                <MaskInput
                  className="w-full"
                  redBorder={errorSoilTillage.length > 0}
                  value={soilTillage}
                  onChange={setSoilTillage}
                  suffix=" %"
                  isBlueBg
                />
                <div className="text-[#FF3B30] text-xs ml-1 mt-1 h-2">
                  {errorSoilTillage && <div> {errorSoilTillage}</div>}
                </div>
              </SummaryCard>
            </>
          )}
        </div>
      )}
    </>
  );
};
